import { Enums, getRenderingEngine, Types } from '@cornerstonejs/core';
import { Enums as ToolEnums,
  utilities } from '@cornerstonejs/tools';
import useMipStore from 'stores/mip';
import useWindowingStore from 'stores/windowing';
import { useCallback, useEffect, useRef } from 'react';
import { calculateSliceValues,
  calculateWindowingValues,
  getAndSetWindowingValues } from 'utils/cornerstone/tooling';
import { setMipDefaultValues } from 'utils/cornerstone/volumeUtils';
import { applyDefaultColorMap, getDefaultLesion } from 'utils/heatmaps/heatmaps';
import { getDefaultWindowingValues, getViewport } from 'utils/utilities';
import cleanUrlParams from 'utils/params';
import useIsVesselReconstructionActive from 'stores/useVesselReconstruction';
import useActiveLesion from 'stores/activeLesion';
import { RENDERING_ENGINE_ID } from '../constants';

export default function useToolingEffects(elementRef, setSliderValue) {
  const { setActiveLesion } = useActiveLesion();
  const { setWindowingValues } = useWindowingStore();
  const { setMipValue } = useMipStore();
  const isHeatMapInitialized = useRef(false);
  const { isVesselReconstructionActive } = useIsVesselReconstructionActive();

  const handleDrag = useCallback(() => {
    getAndSetWindowingValues(setWindowingValues);
  }, []);

  useEffect(() => {
    const defaultLesion = getDefaultLesion();
    const renderingEngine = getRenderingEngine(RENDERING_ENGINE_ID);
    if (renderingEngine && !isHeatMapInitialized.current) {
      applyDefaultColorMap();
      setActiveLesion(defaultLesion);
      renderingEngine.render();
      isHeatMapInitialized.current = true;
    }
  }, [getDefaultLesion()]);

  const handleCameraReset = useCallback(async () => {
    const viewport = getViewport() as Types.IVolumeViewport;
    if (viewport) {
      const windowingValues = calculateWindowingValues()
        || getDefaultWindowingValues(viewport?.getActors()[0]?.uid);
      setWindowingValues(windowingValues);

      const params = new URLSearchParams(window.location.search);
      let currentSlice: number = parseInt(params.get('slice') || '', 10);
      let maxSlice: number = parseInt(params.get('max_slice') || '', 10);
      if ((!currentSlice && !maxSlice) || isVesselReconstructionActive) {
        const currentSliceValues = calculateSliceValues();
        currentSlice = currentSliceValues?.currentSlice || 0;
        maxSlice = currentSliceValues?.maxSlice || 1;
      }
      await utilities.jumpToSlice(viewport.element, {
        imageIndex: currentSlice,
      });
      setSliderValue({
        currentSlice,
        maxSlice,
      });
      setMipDefaultValues(setMipValue);
      // clean up the URL params
      cleanUrlParams(['slice', 'max_slice', 'orientation']);
    }
  }, [setSliderValue, setWindowingValues]);

  const eventListeners = {
    [ToolEnums.Events.MOUSE_DRAG]: handleDrag,
    [ToolEnums.Events.TOUCH_DRAG]: handleDrag,
    [Enums.Events.CAMERA_RESET]: handleCameraReset,
  };

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    Object.entries(eventListeners).forEach(([event, handler]) => {
      element.addEventListener(event, handler);
    });

    // eslint-disable-next-line
    return () => {
      Object.entries(eventListeners).forEach(([event, handler]) => {
        element.removeEventListener(event, handler);
      });
    };
  }, []);

  return handleDrag;
}
